import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useTenantHref } from '../lib/tenantPath.jsx';

// UI-ONLY scaffolding for pharmacist counseling.
// Production wiring:
//   - Backend issues a Twilio Video room token OR Jitsi JWT here.
//   - Frontend uses twilio-video SDK to join the room with local mic + camera.
//   - Recording + transcript stored in counseling_calls.
// This MVP shows local camera only (getUserMedia), records call lifecycle, and
// demonstrates the layout + controls.

export default function VideoCall() {
  const tHref = useTenantHref();
  const { patientId } = useParams();
  const [callType, setCallType] = useState('video');
  const [call, setCall] = useState(null);
  const [stream, setStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [cameraOn, setCameraOn] = useState(true);
  const [notes, setNotes] = useState('');
  const videoRef = useRef(null);

  async function start() {
    const r = await api.startCall({ patient_id: patientId, call_type: callType });
    setCall(r.call);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: callType === 'video',
        audio: true,
      });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
      await api.updateCall(r.call.id, { status: 'in_progress' });
    } catch (e) {
      alert('Camera/mic permission denied: ' + e.message);
    }
  }

  async function end() {
    if (stream) stream.getTracks().forEach(t => t.stop());
    setStream(null);
    if (call) await api.updateCall(call.id, { status: 'completed', notes });
    setCall(null);
  }

  function toggleMute() {
    if (!stream) return;
    stream.getAudioTracks().forEach(t => t.enabled = muted);
    setMuted(!muted);
  }

  function toggleCamera() {
    if (!stream) return;
    stream.getVideoTracks().forEach(t => t.enabled = !cameraOn);
    setCameraOn(!cameraOn);
  }

  useEffect(() => () => { if (stream) stream.getTracks().forEach(t => t.stop()); }, [stream]);

  return (
    <div className="max-w-4xl space-y-4">
      <Link to={tHref(`/patients/${patientId}`)} className="text-sm text-brand-600 hover:underline">← Back to patient</Link>
      <div className="card overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Pharmacist Counseling Call</h1>
            <p className="text-xs text-slate-500">Patient consult — encrypted WebRTC</p>
          </div>
          {!call && (
            <div className="flex gap-2">
              <select className="input w-32" value={callType} onChange={e => setCallType(e.target.value)}>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
              </select>
              <button onClick={start} className="btn-primary">Start call</button>
            </div>
          )}
        </div>
        <div className="relative bg-slate-900 aspect-video">
          <video ref={videoRef} autoPlay playsInline muted
                 className="w-full h-full object-cover" />
          {!stream && (
            <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
              {call ? 'Connecting...' : 'Click "Start call" to begin'}
            </div>
          )}
          {stream && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-slate-800/80 rounded-full px-3 py-2">
              <button onClick={toggleMute} className="text-white text-sm px-3 py-1">{muted ? '🔇 Unmute' : '🎤 Mute'}</button>
              {callType === 'video' && <button onClick={toggleCamera} className="text-white text-sm px-3 py-1">{cameraOn ? '📹 Off' : '📷 On'}</button>}
              <button onClick={end} className="btn-danger text-sm">End call</button>
            </div>
          )}
        </div>
        {call && (
          <div className="p-4 border-t border-slate-200">
            <label className="block text-xs font-medium mb-1">Counseling notes</label>
            <textarea className="input" rows={3} value={notes} onChange={e => setNotes(e.target.value)}
                      placeholder="Document counseling topics, patient questions, follow-up..." />
          </div>
        )}
      </div>
    </div>
  );
}
